import { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Tooltip } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { useParams } from 'react-router';
import { useNavegate } from 'App/hooks';
import {
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  Stack,
} from 'commons/components';
import { useGetShippingRestrictions } from 'domains/Shipping/DeliveryMethods/hooks';
import { DELIVERY_METHODS_ROUTES } from 'domains/Shipping/DeliveryMethods/shippingRoutes';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import DeleteRestrictionModal from './components/DeleteRestrictionModal';
import ShippingRestrictionsTable from './components/ShippingRestrictionsTable';
import useGetDetailActiveApp from '../../EditCarriersPage/useGetDetailActiveApp';
import { allShippingMethodsHaveRestrictions } from '../utils';

function ShippingRestrictionsListPage(): JSX.Element {
  const { id: carrierId } = useParams<{ id: string }>();
  const { goTo } = useNavegate();
  const t = useTranslationShipping('deliveryMethods.shippingLimiting');

  const { restrictions, isLoading, isError, isSuccess, fetchRestrictions } =
    useGetShippingRestrictions();

  const { detailActiveApp, fetchDetailActiveApp } = useGetDetailActiveApp();

  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchDetailActiveApp(carrierId);
    fetchRestrictions(carrierId);
  }, [fetchDetailActiveApp, fetchRestrictions, carrierId]);

  const handleAddRestriction = useCallback(() => {
    goTo(
      DELIVERY_METHODS_ROUTES.newCarrierRestriction.replace(':id', carrierId),
    );
  }, [goTo, carrierId]);

  const handleEdit = useCallback(
    (restrictionId: string) => {
      goTo(
        DELIVERY_METHODS_ROUTES.editCarrierRestriction
          .replace(':id', carrierId)
          .replace(':restrictionId', restrictionId),
      );
    },
    [goTo, carrierId],
  );

  const handleDeleteClick = useCallback((restrictionId: string) => {
    setDeletingId(restrictionId);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    setDeletingId(null);

    if (restrictions && restrictions.length === 1) {
      goTo(DELIVERY_METHODS_ROUTES.carriersDetail.replace(':id', carrierId));
    }

    fetchRestrictions(carrierId);
  }, [fetchRestrictions, carrierId, goTo, restrictions]);

  const goToCarriersDetail = useMemo(
    () => ({
      children: detailActiveApp?.name,
      onClick: () =>
        goTo(DELIVERY_METHODS_ROUTES.carriersDetail.replace(':id', carrierId)),
    }),
    [goTo, carrierId, detailActiveApp],
  );

  const hasAllMethodsWithRestrictions = useMemo(
    () => allShippingMethodsHaveRestrictions(detailActiveApp, restrictions),
    [detailActiveApp, restrictions],
  );

  useEffect(() => {
    if (isSuccess && restrictions && restrictions.length === 0) {
      goTo(DELIVERY_METHODS_ROUTES.carriersDetail.replace(':id', carrierId));
    }
  }, [isSuccess, restrictions, goTo, carrierId]);

  return (
    <IonPageStratus
      width="small"
      headerTop={<HeaderTop navigation={goToCarriersDetail} />}
      headerContent={
        <HeaderContent
          title={t('listPage.title')}
          actions={
            !isLoading && !isError ? (
              <Tooltip
                content={t('listPage.allMethodsHaveRestrictions')}
                hidden={!hasAllMethodsWithRestrictions}
              >
                <Button
                  appearance={
                    hasAllMethodsWithRestrictions ? 'neutral' : 'primary'
                  }
                  onClick={handleAddRestriction}
                  disabled={hasAllMethodsWithRestrictions}
                >
                  <PlusCircleIcon />
                  {t('listPage.addButton')}
                </Button>
              </Tooltip>
            ) : undefined
          }
        />
      }
    >
      {isError && (
        <ErrorScreen
          description={t('listPage.error')}
          onRetry={() => fetchRestrictions(carrierId)}
        />
      )}

      {isLoading && <ShippingRestrictionsTable.Skeleton />}

      {!isLoading && !isError && restrictions && detailActiveApp && (
        <Stack column align="center">
          <ShippingRestrictionsTable
            detailActiveApp={detailActiveApp}
            restrictions={restrictions}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
          />

          {/* <Link as="a" href="#" appearance="primary" textDecoration="none">
            {t('listPage.helpLink')}
            <ExternalLinkIcon />
          </Link> */}
        </Stack>
      )}

      {deletingId !== null && (
        <DeleteRestrictionModal
          restrictionId={deletingId}
          onCancel={() => setDeletingId(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </IonPageStratus>
  );
}

export default ShippingRestrictionsListPage;
